import axios from '../request'
import type {
  BaseResponse,
  UserInfo,
  UserDetailResponse,
  UserInfoCheckResponse,
  UniversityResponse,
  LoginByAccountParams,
  LoginByPhoneParams,
  LoginByWechatParams,
  WechatQrCodeResponse,
  WechatScanResultResponse,
  WechatBindStudentParams,
  WechatBindStudentResponse,
  BindPhoneParams,
  UpdatePasswordParams,
  SendSmsParams,
  VerifySmsParams,
  CheckPhoneParams,
  UpdateUserInfoParams,
  RebindWechatParams,
  WaitJoinClassResponse,
  JoinClassInviteParams,
  JoinClassInviteResponse
} from '../types'

/**
 * 分页查询学校
 */
export function university(params?: {
  page?: number
  size?: number
  nameLike?: string
}) {
  return axios.get<UniversityResponse>('/university/page', { params })
}

/**
 * 获取当前用户信息
 */
export function getUserInfo() {
  return axios.get<BaseResponse<UserInfo>>('/api/user/info')
}

/**
 * 获取当前用户详细信息
 */
export function getCurrentUserDetail(): Promise<FetchResponse> {
  return axios.get('/user/current-detail')
}

/**
 * 检查当前用户信息（如未修改默认密码等）
 */
export function checkMyInformation() {
  return axios.get<UserInfoCheckResponse>('/user/check-my-information')
}

/**
 * 检查手机号是否已在系统中存在【免登录，用租户检查】
 */
export function checkPhone(data: CheckPhoneParams) {
  return axios.post<BaseResponse<{ exist: boolean }>>('/api/user/check-phone', data)
}

/**
 * 验证手机号是否已在租户中绑定
 */
export function verifyPhoneTenantBind(params: {
  phone: string
  tenantCode: string
}) {
  return axios.get<BaseResponse<boolean>>('/user/verify-phone-tenant-bind', { params })
}

/**
 * 通用发送手机验证码【免登录】(旧接口)
 */
export function sendSmsCodeOld(data: SendSmsParams) {
  return axios.post<BaseResponse<void>>('/api/sms/send', data)
}

/**
 * 通用短信验证码验证【免登录】
 */
export function verifySmsCode(data: VerifySmsParams) {
  return axios.post<BaseResponse<boolean>>('/api/sms/verify', data)
}

/**
 * 发送短信验证码
 */
export function sendSmsCode(params: {
  phone: string
  signature?: string
  existFlag?: number
}) {
  return axios.get<BaseResponse<{
    msgId: string
  }>>('/user/send-sms-code', { params })
}

/**
 * 验证短信验证码
 */
export function validSmsCode(params: {
  phone: string
  code: string
}) {
  return axios.get<BaseResponse<boolean>>('/user/valid-sms-code', { params })
}

/**
 * 换绑当前用户手机号
 */
export function rebindPhone(data: {
  phone: string
  msgId: string
}) {
  return axios.put<BaseResponse<void>>('/user/current-rebind-phone', data)
}

/**
 * 换绑当前用户微信
 */
export function rebindWechat(data: RebindWechatParams) {
  return axios.put<BaseResponse<void>>('/user/current-rebind-wx', data)
}

/**
 * 换绑当前用户手机号（旧接口）
 */
export function changePhone(data: BindPhoneParams) {
  return axios.post<BaseResponse<void>>('/api/user/change-phone', data)
}

/**
 * 检查当前用户的信息【如未修改默认密码等】
 */
export function checkUserStatus() {
  return axios.get<BaseResponse<{
    needChangePassword: boolean
    needBindPhone: boolean
    [key: string]: any
  }>>('/api/user/check-status')
}

/**
 * 修改当前账户密码
 */
export function updatePassword(data: UpdatePasswordParams) {
  return axios.put<BaseResponse<void>>('/user/current-update-password', data)
}

/**
 * 更新当前学生用户信息
 */
export function updateUserInfo(data: UpdateUserInfoParams) {
  return axios.put<BaseResponse<UserInfo>>('/user/student/current-update', data)
}

/**
 * 学生端账号密码登录【免登录】
 */
export function loginByAccount(data: LoginByAccountParams & {
  universityCode?: string
  tenant?: string
}) {
  const { universityCode, tenant, ...loginData } = data

  // 将数据转换为 URLSearchParams 格式
  const formData = new URLSearchParams()
  Object.keys(loginData).forEach(key => {
    formData.append(key, (loginData as any)[key])
  })

  return axios.post<BaseResponse<{
    token: string
    userInfo: UserInfo
  }>>('/login', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Tenant': tenant || 'ADMIN',
      'UniversityCode': universityCode || '',
    }
  })
}

/**
 * 获取微信登录二维码ticket
 */
export function getWechatQrCode() {
  return axios.get<WechatQrCodeResponse>('/wx/ticket/login-qr-code', {
    headers: {
      'Tenant': 'ADMIN'
    }
  })
}

/**
 * 获取微信扫码结果
 */
export function getWechatScanResult(sceneId: string) {
  return axios.get<WechatScanResultResponse>('/wx/scan-result', {
    params: { sceneId },
    headers: {
      'Tenant': 'ADMIN'
    }
  })
}

/**
 * 学生端微信绑定学号并登录【免登录】
 */
export function bindWechatStudent(data: WechatBindStudentParams) {
  return axios.post<WechatBindStudentResponse>('/user/student/wx-xcx/bind-phone-login', data, {
    headers: {
      'Tenant': 'ADMIN'
    }
  })
}

/**
 * 学生端微信小程序换绑手机并登录【免登录】
 */
export function changePhoneBindAndLogin(data: {
  rebindKey: string
  password: string
}) {
  return axios.post<BaseResponse<{
    access_token: string
    token_type: string
    scope: string
    expires_in: string
  }>>('/user/student/wx-xcx/change-phone-bind-login', data, {
    headers: {
      'Tenant': 'ADMIN'
    }
  })
}

/**
 * 换绑当前用户微信【需要登录】
 */
export function rebindCurrentUserWechat(data: {
  sceneId: string
  wxOpenId: string
}) {
  return axios.put<BaseResponse<any>>('/user/current-rebind-wx', data)
}

/**
 * 学生端微信小程序一键登陆【免登录】
 * 根据 小程序临时登录凭证 code 换取 Token
 */
export function loginByWechat(data: LoginByWechatParams) {
  return axios.post<BaseResponse<{
    token: string
    userInfo: UserInfo
    needBind?: boolean
  }>>('/api/auth/login/wechat', data)
}

/**
 * 学生端微信小程序换绑微信并登录【免登录】
 */
export function changeWechatAndLogin(data: LoginByWechatParams) {
  return axios.post<BaseResponse<{
    token: string
    userInfo: UserInfo
  }>>('/api/auth/login/wechat/change', data)
}

/**
 * 学生端Web端手机登录【免登录】
 */
export function loginByPhone(data: {
  phone: string
  smsCode: string
}) {
  return axios.post<BaseResponse<{
    resultCode: 'LOGIN' | 'USER_DISABLED' | 'NEED_BIND'
    bindKey?: string
    loginToken?: {
      access_token: string
      token_type: string
      scope: string
      expires_in: string
    }
  }>>('/user/student/web/auth-phone', data)
}

/**
 * 学生端Web端绑定手机并登录【免登录】
 */
export function bindPhoneAndLogin(data: {
  bindKey: string
  universityCode: string
  employeeNum: string
  name: string
}) {
  return axios.post<BaseResponse<{
    resultCode: 'LOGIN' | 'NAME_NOT_MATCH' | 'BIND_OTHER'
    originPhoneDim?: string
    loginToken?: {
      access_token: string
      token_type: string
      scope: string
      expires_in: string
    }
  }>>('/user/student/web/bind-phone-login', data)
}

/**
 * 学生端微信小程序换绑手机并登录【免登录】
 */
export function changePhoneAndLogin(data: BindPhoneParams) {
  return axios.post<BaseResponse<{
    token: string
    userInfo: UserInfo
  }>>('/api/auth/login/phone/change', data)
}

/**
 * 获取待加入的班级课程列表
 */
export function getWaitJoinClassList() {
  return axios.get<WaitJoinClassResponse>('/student/wait-join-class')
}

/**
 * 学生是否接受班级邀请
 */
export function joinClassInvite(data: JoinClassInviteParams) {
  return axios.post<JoinClassInviteResponse>('/student/join', data)
}

